/**
 * Registry của các merge field dùng trong mail template ("Add fields").
 *
 * Template lưu placeholder bằng NHÃN TIẾNG VIỆT thay vì tên column trong DB, để người
 * soạn template hiểu được field đó là gì:
 *      {{Tên ứng viên}}        <=> column full_name
 *      {{Gọi UV là (Email)}}   <=> column caller_name_to_email
 *      {{User xưng là (Email)}}<=> column vocative
 *
 * Placeholder có thể kèm modifier để đổi hoa/thường của GIÁ TRỊ sau khi fill:
 *      {{Tên ứng viên}}             -> giữ nguyên giá trị gốc
 *      {{Tên ứng viên|capitalize}}  -> Viết Hoa Chữ Cái Đầu
 *      {{Tên ứng viên|upper}}       -> IN HOA TOÀN BỘ
 *      {{Tên ứng viên|lower}}       -> in thường toàn bộ
 *
 * File này KHÔNG import gì để có thể dùng chung giữa plugin SunEditor và CommonUtils.
 */

export const TextCase = {
    KEEP: '',
    CAPITALIZE: 'capitalize',
    UPPER: 'upper',
    LOWER: 'lower',
};

/** Các lựa chọn hiển thị trong submenu "Add fields". */
export const TEXT_CASE_OPTIONS = [
    {value: TextCase.KEEP, label: 'Giữ nguyên'},
    {value: TextCase.CAPITALIZE, label: 'Viết Hoa Đầu'},
    {value: TextCase.UPPER, label: 'IN HOA'},
];

/** label: hiển thị cho user + lưu trong template. key: tên column khi merge giá trị. */
export const MAIL_MERGE_FIELDS = [
    {key: 'caller_name_to_email', label: 'Gọi UV là (Email)'},
    {key: 'vocative', label: 'User xưng là (Email)'},
    {key: 'full_name', label: 'Tên ứng viên'},
];

const toStr = (value) => String(value === undefined || value === null ? '' : value);

/**
 * Nhãn tiếng Việt có DẤU CÁCH, mà template được lưu dưới dạng HTML của editor:
 * contenteditable hay đổi space thành &nbsp; ( ), chèn ký tự zero-width, hoặc bọc
 * một phần nhãn bằng thẻ định dạng khi user bôi đậm/đổi màu cả dòng. Nếu so khớp thô thì
 * placeholder sẽ không khớp nhãn nào và giá trị bị fill thành rỗng — mất dữ liệu âm thầm.
 * Vì vậy luôn làm sạch tên field trước khi tra bảng.
 */
const cleanName = (name) => toStr(name)
    .replace(/<[^>]*>/g, '')                            // thẻ HTML lọt vào giữa nhãn
    .replace(/&nbsp;|&#0*160;|&#x0*a0;/gi, ' ')         // &nbsp; dạng entity
    .replace(/[\u00A0\u2007\u202F]/g, ' ')          // nbsp / narrow-nbsp dạng ký tự
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, '')    // zero-width (suneditor chèn sau mỗi tag)
    .replace(/\s+/g, ' ')
    .trim();

const normalize = (name) => cleanName(name).toLowerCase();

const LABEL_TO_KEY = MAIL_MERGE_FIELDS.reduce((acc, field) => {
    acc[normalize(field.label)] = field.key;
    return acc;
}, {});

/**
 * Nhãn tiếng Việt -> tên column. Nếu không khớp nhãn nào thì trả lại chính chuỗi đó, nhờ vậy
 * các template cũ lưu bằng tên column ({{full_name}}) vẫn merge được như trước.
 */
export const resolveMergeFieldKey = (name) => {
    const cleaned = cleanName(name);
    return LABEL_TO_KEY[normalize(cleaned)] || cleaned;
};

/** Tách "Tên ứng viên|upper" thành {name: 'Tên ứng viên', textCase: 'upper'}. */
export const parseMergeToken = (token) => {
    const raw = toStr(token);
    const separatorIndex = raw.indexOf('|');
    if (separatorIndex < 0) {
        return {name: cleanName(raw), textCase: TextCase.KEEP};
    }
    return {
        name: cleanName(raw.slice(0, separatorIndex)),
        textCase: cleanName(raw.slice(separatorIndex + 1)).toLowerCase(),
    };
};

/** Dựng placeholder để chèn vào editor. */
export const buildMergeToken = (label, textCase) => (
    textCase ? `{{${label}|${textCase}}}` : `{{${label}}}`
);

export const applyTextCase = (value, textCase) => {
    const text = value === undefined || value === null ? '' : String(value);
    if (!text) return '';

    switch (textCase) {
        case TextCase.UPPER:
            return text.toUpperCase();
        case TextCase.LOWER:
            return text.toLowerCase();
        case TextCase.CAPITALIZE:
            // Hoa chữ cái đầu mỗi từ, phần còn lại về thường -> "NGUYEN VAN A" thành "Nguyen Van A"
            return text.toLowerCase().replace(/(?:^|\s|["'([{])+\S/g, (match) => match.toUpperCase());
        default:
            return text;
    }
};
